import type { ISRLeadSubstatus, ISRNotesDocument, ISRSalesStatus, ISRUpdateSnapshot, MappedLeadData } from '../types';

/**
 * Pure cache-patching primitives for ISR saves. Shared by useISRLeadMutation
 * (optimistic/onSuccess cache writes) and utils/isrPendingWrites (defending
 * saved values against refetches that read the leads source before n8n's
 * write-back lands there). Single source of truth — never duplicate these
 * field maps in consumers.
 */

export const isRecord = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const isIdentifier = (value: unknown): value is string | number =>
  (typeof value === 'string' && value.trim().length > 0) ||
  (typeof value === 'number' && Number.isFinite(value));

export const normalizeIdentifier = (value: unknown): string | null =>
  isIdentifier(value) ? String(value) : null;

const cloneNotes = (document: ISRNotesDocument): ISRNotesDocument => ({
  schema_version: 1,
  notes: document.notes.map((note) => ({ ...note })),
});

export const normalizeSalesStatus = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  // OUTREACHED is now its own top-level status — no remapping.
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

/**
 * Map the stepper's (status, substatus) pair onto the `lead_status` value the
 * n8n update_campaign action detects: outreaching | outreached | engaged |
 * proposal | converted | discarded. OUTREACHED is now a top-level status in both the UI
 * and the database, so the pair passes through unchanged.
 */
export const toWireSalesStatus = (
  status: ISRSalesStatus,
  substatus: ISRLeadSubstatus | null,
): ISRSalesStatus | 'outreached' => status;

export const readHydratedPersonId = (lead: MappedLeadData | null | undefined): string | number | null => {
  const value = lead?.personLvl1?.id ?? lead?.rawOriginalData?.lvl1_person?.id;
  return isIdentifier(value) ? value : null;
};

export const readHydratedCompanyId = (lead: MappedLeadData | null | undefined): string | number | null => {
  const value = lead?.companyLvl1?.id ?? lead?.rawOriginalData?.lvl1_company?.id;
  return isIdentifier(value) ? value : null;
};

/** True for the diff field names that represent a sales status/substatus change. */
export const isSalesField = (field: string): boolean => {
  const normalized = field.trim().toLowerCase();
  return normalized === 'status' ||
    normalized === 'substatus' ||
    normalized.endsWith('.lead_status') ||
    normalized.endsWith('.lead_substatus') ||
    normalized === 'lead_status' ||
    normalized === 'lead_substatus';
};

export const leadRecordMatches = (record: Record<string, any>, lvl2Id: string | number): boolean => {
  const targetId = String(lvl2Id);
  const candidates = [
    record.id,
    record.lead_id,
    record.lvl2_id,
    record.personLvl2?.id,
    record.rawOriginalData?.lvl2?.id,
  ];

  return candidates.some((candidate) =>
    isIdentifier(candidate) && String(candidate) === targetId
  );
};

export const patchPersonRecord = (
  current: unknown,
  snapshot: ISRUpdateSnapshot,
  includeLvl2Fields: boolean,
  updatedAt: string,
): Record<string, any> => {
  const base = isRecord(current) ? current : {};
  const next: Record<string, any> = {
    ...base,
    name: snapshot.person.full_name,
    email_id: snapshot.person.email_id,
    phone_id: snapshot.person.phone_id,
    mobile_id: snapshot.person.mobile_id,
    whatsapp_id: snapshot.person.whatsapp_id,
    // Social profiles — canonical lvl1/lvl2 channel keys. Omitting these made
    // saved social edits vanish from re-derived views (deriveContact falls
    // back to the stale record the moment the cache patch bumps updatedAt).
    linkedin_id: snapshot.person.linkedin_id,
    twitter_x_id: snapshot.person.twitter_x_id,
    instagram_id: snapshot.person.instagram_id,
    facebook_id: snapshot.person.facebook_id,
    updated_at: updatedAt,
  };

  if (!includeLvl2Fields) {
    next.id = snapshot.person.id;
    // Person lvl1 location — mirrors the company-record patch convention.
    next.country = snapshot.person.country;
    next.state = snapshot.person.state;
    next.city = snapshot.person.city;
    next.full_address = snapshot.person.full_address;
    if (snapshot.lead_status !== null) {
      // Mirror the value n8n stores (OUTREACHED lifts to its own status).
      next.status = toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus);
    }
    return next;
  }

  next.lead_substatus = snapshot.lead_substatus;
  next.notes = cloneNotes(snapshot.notes);
  // Role/position — lvl2-only attribute (the lead's own row), never on lvl1.
  next.role_position = snapshot.person.role_position;
  // Person location — lvl2 row carries it too and WINS on display precedence
  // (lvl2 → lvl1), so patch both branches to keep the card in sync.
  next.country = snapshot.person.country;
  next.state = snapshot.person.state;
  next.city = snapshot.person.city;
  next.full_address = snapshot.person.full_address;
  next.company_email_id = snapshot.company.email_id;
  next.company_phone_id = snapshot.company.phone_id;
  next.company_mobile_id = snapshot.company.mobile_id;
  next.company_whatsapp_id = snapshot.company.whatsapp_id;
  next.company_website_id = snapshot.company.website_id;
  next.company_nr_employees = snapshot.company.nr_employees;
  next.cnae_code = snapshot.company.cnae_code;
  next.company_country = snapshot.company.country;
  next.company_state = snapshot.company.state;
  next.company_city = snapshot.company.city;
  next.company_full_address = snapshot.company.full_address;
  if (snapshot.lead_status !== null) {
    const wireStatus = toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus);
    next.status = wireStatus;
    next.lead_status = wireStatus;
  }

  return next;
};

export const patchCompanyRecord = (
  current: unknown,
  snapshot: ISRUpdateSnapshot,
  updatedAt: string,
): Record<string, any> => ({
  ...(isRecord(current) ? current : {}),
  id: snapshot.company.id,
  email_id: snapshot.company.email_id,
  phone_id: snapshot.company.phone_id,
  mobile_id: snapshot.company.mobile_id,
  whatsapp_id: snapshot.company.whatsapp_id,
  website_id: snapshot.company.website_id,
  nr_employees: snapshot.company.nr_employees,
  // Social + revenue — editable fields whose saved values previously fell
  // back to stale data because the patch never covered them.
  linkedin_id: snapshot.company.linkedin_id,
  twitter_x_id: snapshot.company.twitter_x_id,
  instagram_id: snapshot.company.instagram_id,
  facebook_id: snapshot.company.facebook_id,
  revenue: snapshot.company.revenue_range,
  revenue_range: snapshot.company.revenue_range,
  cnae_code: snapshot.company.cnae_code,
  country: snapshot.company.country,
  state: snapshot.company.state,
  city: snapshot.company.city,
  full_address: snapshot.company.full_address,
  updated_at: updatedAt,
});

const patchRawOriginalData = (
  current: unknown,
  snapshot: ISRUpdateSnapshot,
  updatedAt: string,
): unknown => {
  if (!isRecord(current)) return current;

  return {
    ...current,
    notes: cloneNotes(snapshot.notes),
    lead_substatus: snapshot.lead_substatus,
    updated_at: updatedAt,
    ...(snapshot.lead_status !== null
      ? {
          status: toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus),
          lead_status: toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus),
        }
      : {}),
    lvl2: patchPersonRecord(current.lvl2, snapshot, true, updatedAt),
    lvl1_person: patchPersonRecord(current.lvl1_person, snapshot, false, updatedAt),
    lvl1_company: patchCompanyRecord(current.lvl1_company, snapshot, updatedAt),
  };
};

export const patchLeadRecord = <T extends Record<string, any>>(
  record: T,
  snapshot: ISRUpdateSnapshot,
  updatedAt: string,
): T => {
  const employeeCount = snapshot.company.nr_employees;
  const notes = cloneNotes(snapshot.notes);
  const hasFullLeadShape =
    'personLvl2' in record ||
    'personLvl1' in record ||
    'companyLvl1' in record ||
    'rawOriginalData' in record;

  const patched: Record<string, any> = {
    ...record,
    leadName: snapshot.person.full_name ?? '',
    // Role — feeds the card tile and deriveContact's seed (lvl2 wins).
    leadRole: snapshot.person.role_position ?? '',
    // Person location — feeds the list's lead-location line under the role.
    leadCity: snapshot.person.city ?? '',
    leadState: snapshot.person.state ?? '',
    leadCountry: snapshot.person.country ?? '',
    email: snapshot.person.email_id ?? '',
    phone: snapshot.person.phone_id ?? '',
    mobile: snapshot.person.mobile_id ?? '',
    leadWhatsapp: snapshot.person.whatsapp_id ?? '',
    // Person socials — transformer precedence is lvl2 → lvl1, and the flat
    // card fields feed both the card tiles and deriveContact's seeds.
    leadLinkedin: snapshot.person.linkedin_id ?? '',
    leadTwitter: snapshot.person.twitter_x_id ?? '',
    leadInstagram: snapshot.person.instagram_id ?? '',
    leadFacebook: snapshot.person.facebook_id ?? '',
    companyEmail: snapshot.company.email_id ?? '',
    companyPhone: snapshot.company.phone_id ?? '',
    companyMobile: snapshot.company.mobile_id ?? '',
    companyWhatsapp: snapshot.company.whatsapp_id ?? '',
    companyWebsite: snapshot.company.website_id ?? '',
    // Company socials — transformer reads company_* lvl2 keys first, so keep
    // them in sync with the canonical lvl1 values the snapshot carries.
    companyLinkedin: snapshot.company.linkedin_id ?? '',
    companyTwitter: snapshot.company.twitter_x_id ?? '',
    companyInstagram: snapshot.company.instagram_id ?? '',
    companyFacebook: snapshot.company.facebook_id ?? '',
    // Revenue — the card's revenue badge reads the flat field; lvl1_company
    // stores it as `revenue`/`revenue_range` (both patched there).
    revenue: snapshot.company.revenue_range ?? '',
    revenueRange: snapshot.company.revenue_range ?? '',
    cnae_code: snapshot.company.cnae_code ?? '',
    companyCountry: snapshot.company.country ?? '',
    companyState: snapshot.company.state ?? '',
    companyCity: snapshot.company.city ?? '',
    companyAddress: snapshot.company.full_address ?? '',
    fullAddress: snapshot.company.full_address ?? '',
    companyEmployees: employeeCount ?? '',
    nr_employees: employeeCount,
    company_nr_employees: employeeCount,
    employee_count: employeeCount,
    salesSubstatus: snapshot.lead_substatus,
    lead_substatus: snapshot.lead_substatus,
    isrNotes: notes,
    isrNotesParseError: false,
    updatedAt,
    ...(snapshot.lead_status !== null
      ? {
          status: toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus),
          lead_status: toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus),
        }
      : {}),
  };

  if (hasFullLeadShape) {
    patched.personLvl2 = patchPersonRecord(record.personLvl2, snapshot, true, updatedAt);
    patched.personLvl1 = patchPersonRecord(record.personLvl1, snapshot, false, updatedAt);
    patched.companyLvl1 = patchCompanyRecord(record.companyLvl1, snapshot, updatedAt);
    patched.rawOriginalData = patchRawOriginalData(record.rawOriginalData, snapshot, updatedAt);
  }

  return patched as T;
};

/** Patch arrays plus the list/infinite-query envelopes used by lead views. */
export const patchCampaignListData = (
  current: unknown,
  lvl2Id: string | number,
  snapshot: ISRUpdateSnapshot,
  updatedAt: string,
): unknown => {
  if (Array.isArray(current)) {
    return current.map((item) => {
      if (isRecord(item) && leadRecordMatches(item, lvl2Id)) {
        return patchLeadRecord(item, snapshot, updatedAt);
      }

      if (Array.isArray(item) || isRecord(item)) {
        return patchCampaignListData(item, lvl2Id, snapshot, updatedAt);
      }

      return item;
    });
  }

  if (!isRecord(current)) return current;
  if (leadRecordMatches(current, lvl2Id)) {
    return patchLeadRecord(current, snapshot, updatedAt);
  }

  let changed = false;
  const patched = { ...current };
  for (const key of ['leads', 'items', 'data', 'pages'] as const) {
    if (!(key in current)) continue;
    const nextValue = patchCampaignListData(current[key], lvl2Id, snapshot, updatedAt);
    if (nextValue !== current[key]) {
      patched[key] = nextValue;
      changed = true;
    }
  }

  return changed ? patched : current;
};

export const patchDetailData = (
  current: unknown,
  fallbackLead: MappedLeadData | null | undefined,
  lvl2Id: string | number,
  snapshot: ISRUpdateSnapshot,
  updatedAt: string,
): unknown => {
  const source = current ?? fallbackLead;
  if (!source) return source;

  if (isRecord(source) && 'data' in source && !leadRecordMatches(source, lvl2Id)) {
    const patchedData = patchCampaignListData(source.data, lvl2Id, snapshot, updatedAt);
    return patchedData === source.data ? source : { ...source, data: patchedData };
  }

  return isRecord(source)
    ? patchLeadRecord(source, snapshot, updatedAt)
    : source;
};
